// src/components/admin/settings/FeatureSettings.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Settings } from 'lucide-react';
import styles from '../../../styles/Settings.module.css';
import useTranslation from '../../../hooks/useTranslation';
import { updateFeatures, selectFeatures } from '../../../store/slices/platformSettingsSlice';
import toast from 'react-hot-toast';

const FeatureSettings = () => {
  const dispatch = useDispatch();
  const featuresRedux = useSelector(selectFeatures);

  const { translatedText: platformFeatures } = useTranslation('Platform Features');
  const { translatedText: allowPublicDonations } = useTranslation('Allow Public Donations');
  const { translatedText: allowPublicDonationsDesc } = useTranslation('Enable or disable donation collection');
  const { translatedText: showDonationProgress } = useTranslation('Show Donation Progress');
  const { translatedText: showDonationProgressDesc } = useTranslation('Display donation progress bars on the platform');
  const { translatedText: allowAnonymousDonations } = useTranslation('Allow Anonymous Donations');
  const { translatedText: allowAnonymousDonationsDesc } = useTranslation('Allow donors to remain anonymous');
  const { translatedText: providerVerification } = useTranslation('Provider Verification');
  const { translatedText: providerVerificationDesc } = useTranslation('Require verification for service providers');
  const { translatedText: reviewSystem } = useTranslation('Review System');
  const { translatedText: reviewSystemDesc } = useTranslation('Allow clients to leave reviews and ratings');

  const [localFeatures, setLocalFeatures] = React.useState(
    featuresRedux || {
      allowPublicDonations: true,
      showDonationProgress: true,
      allowAnonymousDonations: true,
      providerVerification: true,
      reviewSystem: true
    }
  );

  React.useEffect(() => {
    if (featuresRedux) {
      setLocalFeatures(featuresRedux);
    }
  }, [featuresRedux]);

  const handleChange = (field, value) => {
    setLocalFeatures(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await dispatch(updateFeatures(localFeatures)).unwrap();
      toast.success('Feature settings saved!');
    } catch (error) {
      toast.error(error || 'Failed to save settings');
    }
  };

  return (
    <div className={styles.settingsCard}>
      <h3>
        <Settings size={20} />
        {platformFeatures || 'Platform Features'}
      </h3>
      <div className={styles.settingsForm}>
        <div className={styles.toggleGroup}>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localFeatures.allowPublicDonations}
              onChange={(e) => handleChange('allowPublicDonations', e.target.checked)}
            />
            {allowPublicDonations || 'Allow Public Donations'}
            <span className={styles.toggleDescription}>{allowPublicDonationsDesc || 'Enable or disable donation collection'}</span>
          </label>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localFeatures.showDonationProgress}
              onChange={(e) => handleChange('showDonationProgress', e.target.checked)}
            />
            {showDonationProgress || 'Show Donation Progress'}
            <span className={styles.toggleDescription}>{showDonationProgressDesc || 'Display donation progress bars on the platform'}</span>
          </label>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localFeatures.allowAnonymousDonations}
              onChange={(e) => handleChange('allowAnonymousDonations', e.target.checked)}
            />
            {allowAnonymousDonations || 'Allow Anonymous Donations'}
            <span className={styles.toggleDescription}>{allowAnonymousDonationsDesc || 'Allow donors to remain anonymous'}</span>
          </label>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localFeatures.providerVerification}
              onChange={(e) => handleChange('providerVerification', e.target.checked)}
            />
            {providerVerification || 'Provider Verification'}
            <span className={styles.toggleDescription}>{providerVerificationDesc || 'Require verification for service providers'}</span>
          </label>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={localFeatures.reviewSystem}
              onChange={(e) => handleChange('reviewSystem', e.target.checked)}
            />
            {reviewSystem || 'Review System'}
            <span className={styles.toggleDescription}>{reviewSystemDesc || 'Allow clients to leave reviews and ratings'}</span>
          </label>
        </div>
        <button className={styles.saveBtn} onClick={handleSave}>Save Changes</button>
      </div>
    </div>
  );
};

export default FeatureSettings;